import AppDialog from '../../../../../components/common/AppDialog';
import {SVG} from '../../../../../constants/assetLists';
import AppMUITextInput from '../../../../../components/common/AppMUITextInput';
import {Radio} from '@mui/material';
import AppTextInput from '../../../../../components/common/AppTextInput';
import AppButton from '../../../../../components/common/AppButton';
import {Controller, useForm} from 'react-hook-form';
import z from 'zod';
import {zodResolver} from '@hookform/resolvers/zod';
import AppMUIMenuItem from '../../../../../components/common/AppMUIMenuItem';
import {useCustomTiming} from '../../../../../context/CustomTimingContextProvider';
import {useEffect} from 'react';
import {DateInput} from './components/DateInput';
import {DAYS} from '../../../../../utils';
import AppMUIAutocomplete from '../../../../../components/common/AppMUIAutoComplete';

type Props = {
  open: boolean;
  onClose: () => void;
  onCancel: () => void;
  onSubmit: (data: FormValues) => void;
};

type FormValues = {
  repeatEvery: number;
  repeatType: string;
  weeklyDays: number[];
  monthlyDates: number[];
  endCondition: string;
  endDate?: string;
  occurrences?: number;
};

const REPEATS = [
  {label: 'שבועות', value: 'weekly'},
  {label: 'חודשים', value: 'monthly'},
  // {label: 'שנים', value: 'year'},
];

// Generate monthly dates 1-31
const MONTHLY_DATES = Array.from({length: 31}, (_, index) => ({
  label: `${index + 1}`,
  value: index + 1,
}));

const REPETITIONS = Array.from({length: 100}, (_, index) => ({
  label: `${index + 1} חזרות`,
  value: index + 1,
}));

const zodSchema = z
  .object({
    repeatEvery: z.number().min(1, {message: 'שדה חובה!'}),
    repeatType: z.string({message: 'שדה חובה!'}).min(1, 'שדה חובה!'),
    weeklyDays: z.array(z.number()),
    monthlyDates: z.array(z.number()),
    endCondition: z.string({message: 'שדה חובה!'}).min(1, 'שדה חובה!'),
    endDate: z.string().optional(),
    occurrences: z.number().min(1, {message: 'שדה חובה!'}).optional(),
  })
  .refine(
    data => {
      if (data.endCondition === 'end_date' && !data.endDate) return false;
      return true;
    },
    {
      message: 'שדה חובה!',
      path: ['endDate'],
    },
  )
  .refine(
    data => {
      if (data.endCondition === 'occurrences' && !data.occurrences)
        return false;
      return true;
    },
    {
      message: 'שדה חובה!',
      path: ['occurrences'],
    },
  )
  .refine(
    data => {
      if (data.repeatType === 'weekly' && data.weeklyDays.length === 0)
        return false;
      return true;
    },
    {
      message: 'יש לבחור לפחות יום אחד',
      path: ['weeklyDays'],
    },
  )
  .refine(
    data => {
      if (data.repeatType === 'monthly' && data.monthlyDates.length === 0)
        return false;
      return true;
    },
    {
      message: 'יש לבחור לפחות תאריך אחד',
      path: ['monthlyDates'],
    },
  );

function CustomTiming({open, onClose, onSubmit, onCancel}: Props) {
  const {customTimingData, setCustomTimingData} = useCustomTiming();

  const {
    formState: {errors},
    reset,
    control,
    setValue,
    watch,
    handleSubmit,
    getValues,
  } = useForm<FormValues>({
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    defaultValues: {
      repeatEvery: 1,
      repeatType: 'weekly',
      weeklyDays: customTimingData?.weeklyDays || [],
      monthlyDates: [],
      endCondition: 'never',
      endDate: '',
      occurrences: 1,
    },
    resolver: zodResolver(zodSchema),
  });

  // Load saved data when modal opens
  useEffect(() => {
    if (open && customTimingData) {
      reset(customTimingData);
    }
  }, [open, customTimingData, reset]);

  const renderArrows = (
    field: 'repeatEvery' | 'occurrences',
    disabled = false,
  ) => {
    const handleIncDec = (type: 'inc' | 'dec') => {
      const currentValue = Number(getValues(field)) || 0;
      if (type === 'inc') {
        setValue(field, currentValue + 1);
      } else {
        setValue(field, currentValue > 1 ? currentValue - 1 : currentValue);
      }
    };

    return (
      <div className="flex flex-col gap-3 items-center">
        <button
          className="w-[14px] h-[7px] transform rotate-180 hover:opacity-40 transition-opacity duration-200"
          disabled={disabled}
          onClick={() => handleIncDec('inc')}>
          <img
            src={SVG.dropdownIcon}
            alt="icon"
            className="w-full h-full object-contain"
          />
        </button>

        <button
          className="w-[14px] h-[7px] hover:opacity-40 transition-opacity duration-200"
          disabled={disabled}
          onClick={() => handleIncDec('dec')}>
          <img
            src={SVG.dropdownIcon}
            alt="icon"
            className="w-full h-full object-contain"
          />
        </button>
      </div>
    );
  };

  const renderWeeklyDays = (selectedDays: number[]) => {
    const handleDayClick = (dayIndex: number) => {
      const isSelected = selectedDays.includes(dayIndex);
      if (isSelected) {
        // Deselect the day
        setValue(
          'weeklyDays',
          selectedDays.filter(d => d !== dayIndex),
        );
      } else {
        // Add this day to the selection
        setValue('weeklyDays', [...selectedDays, dayIndex]);
      }
    };

    return (
      <div className="flex flex-wrap items-center gap-2">
        {DAYS.map(item => {
          const isActive = selectedDays.includes(item.index);
          return (
            <button
              key={item.label}
              className={`w-[48px] h-[48px] rounded-full flex justify-center items-center ${
                isActive ? 'bg-linkBlue text-white' : 'bg-grayBg text-black'
              }`}
              onClick={() => handleDayClick(item.index)}>
              <span className="text-xl font-medium">{item.label}</span>
            </button>
          );
        })}
      </div>
    );
  };

  const renderMonthlyDates = (selectedDates: number[]) => {
    const handleDateClick = (date: number) => {
      const isSelected = selectedDates.includes(date);
      if (isSelected) {
        setValue(
          'monthlyDates',
          selectedDates.filter(d => d !== date),
        );
      } else {
        setValue('monthlyDates', [...selectedDates, date]);
      }
    };

    return (
      <div className="flex flex-wrap items-center gap-2 max-h-[200px] overflow-auto">
        {MONTHLY_DATES.map(item => {
          const isActive = selectedDates.includes(item.value);
          return (
            <button
              key={item.label}
              className={`w-[40px] h-[40px] rounded-full flex justify-center items-center ${
                isActive ? 'bg-linkBlue text-white' : 'bg-grayBg text-black'
              }`}
              onClick={() => handleDateClick(item.value)}>
              <span className="text-sm font-medium">{item.label}</span>
            </button>
          );
        })}
      </div>
    );
  };

  const renderRadios = (
    value: string,
    fieldValue: 'never' | 'end_date' | 'occurrences',
    label: string,
  ) => {
    const handleChange = () => {
      setValue('endCondition', fieldValue);
      setValue('endDate', '');
      if (fieldValue === 'occurrences') {
        setValue('occurrences', 1);
      }
    };

    return (
      <div className="w-[230px] flex items-center gap-5">
        <Radio
          color="secondary"
          checked={value === fieldValue}
          value={fieldValue}
          onChange={handleChange}
        />
        <p className="m-0 text-xl font-medium text-black">{label}</p>
      </div>
    );
  };

  const handleFormSubmit = (data: FormValues) => {
    onSubmit(data);
    handleModalClose();
  };

  const handleModalClose = () => {
    const currentFormState = getValues();
    setCustomTimingData(currentFormState);
    onClose();
  };
  const handleCancel = () => {
    reset();
    onCancel();
  };
  const currentRepeatType = watch('repeatType');

  return (
    <AppDialog
      showModel={open}
      onClose={handleModalClose}
      title="תזמון מותאם"
      titleColor="#000"
      titleFontSize="32px"
      titleFontWeight="400"
      showCloseIcon={false}
      showHeaderLine={false}
      width={592}
      containerClasses="overflow-hidden">
      <div className="flex flex-col gap-5 pb-3">
        <div className="flex flex-col gap-3 max-h-[400px] overflow-auto px-[32px]">
          <div className="flex flex-wrap items-center gap-4">
            <p className="m-0 text-xl font-medium text-black">
              חוזר על עצמו כל
            </p>

            <div className="flex items-center gap-4">
              <Controller
                name="repeatEvery"
                control={control}
                render={({field}) => (
                  <>
                    <div className="w-[60px] h-[60px] bg-grayBg rounded-md flex justify-center items-center">
                      <AppTextInput
                        {...field}
                        type="number"
                        containerClassName="!bg-transparent"
                        className="border-none !bg-transparent [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none [-moz-appearance:textfield] text-xl font-medium text-center"
                      />
                    </div>

                    {renderArrows('repeatEvery')}
                  </>
                )}
              />

              <Controller
                name="repeatType"
                control={control}
                render={({field: {value, onChange}}) => (
                  <AppMUITextInput
                    variant="filled"
                    style={{width: 162}}
                    size="small"
                    select
                    value={value}
                    onChange={onChange}
                    error={!!errors.repeatType}
                    helperText={errors.repeatType?.message}>
                    {REPEATS.map((item, index) => {
                      return (
                        <AppMUIMenuItem key={index} value={item.value}>
                          {item.label}
                        </AppMUIMenuItem>
                      );
                    })}
                  </AppMUITextInput>
                )}
              />
            </div>
          </div>

          {/* Conditional rendering based on repeat type */}
          {currentRepeatType === 'weekly' && (
            <div className="flex flex-wrap items-center gap-4">
              <p className="m-0 text-xl font-medium text-black">ימים</p>

              <Controller
                name="weeklyDays"
                control={control}
                render={({field: {value}}) => renderWeeklyDays(value || [])}
              />
              {errors.weeklyDays && (
                <p className="text-red-500 text-sm">
                  {errors.weeklyDays.message}
                </p>
              )}
            </div>
          )}

          {currentRepeatType === 'monthly' && (
            <div className="flex flex-col gap-3">
              <p className="m-0 text-xl font-medium text-black">
                תאריכים בחודש
              </p>

              <Controller
                name="monthlyDates"
                control={control}
                render={({field: {value}}) => renderMonthlyDates(value || [])}
              />
              {errors.monthlyDates && (
                <p className="text-red-500 text-sm">
                  {errors.monthlyDates.message}
                </p>
              )}
            </div>
          )}

          <div className="flex flex-col gap-3">
            <p className="m-0 text-xl font-medium text-black">מסתיים</p>

            <Controller
              name="endCondition"
              control={control}
              render={({field: {value}}) =>
                renderRadios(value as string, 'never', 'ממשיך ללא סיום')
              }
            />

            <div className="flex flex-wrap items-center extraSmall:gap-2 md:gap-5">
              <Controller
                name="endCondition"
                control={control}
                render={({field: {value}}) =>
                  renderRadios(value as string, 'end_date', 'בתאריך')
                }
              />

              <Controller
                name="endDate"
                control={control}
                render={({field: {value, onChange}}) => (
                  <DateInput
                    dateFormat="EEEE, d 'ב'MMMM"
                    placeholderText="yyyy, mmm, dd"
                    selected={value ? new Date(value) : null}
                    onChange={date => onChange(date?.toISOString() || '')}
                    disabled={watch().endCondition !== 'end_date'}
                  />
                )}
              />
            </div>

            <div className="flex flex-wrap items-center extraSmall:gap-2 md:gap-5">
              <Controller
                name="endCondition"
                control={control}
                render={({field: {value}}) =>
                  renderRadios(value as string, 'occurrences', 'אחרי')
                }
              />
            </div>
            <Controller
              name="occurrences"
              control={control}
              render={({field: {value, onChange}}) => (
                <div className="flex items-center gap-3 w-1/2 mb-10">
                  <AppMUIAutocomplete
                    size="small"
                    options={REPETITIONS}
                    fullWidth
                    getOptionLabel={option => option.label}
                    disabled={watch().endCondition !== 'occurrences'}
                    value={
                      REPETITIONS.find(item => item.value === value) || null
                    }
                    onChange={(_, newValue) => {
                      const selectedValue = newValue?.value || 1;
                      onChange(selectedValue);
                    }}
                    ListboxProps={{
                      sx: {
                        maxHeight: 200,
                      },
                    }}
                    error={!!errors.occurrences}
                    helperText={errors.occurrences?.message}
                  />
                  {/* {renderArrows(
                    'occurrences',
                    value as number,
                    watch().endCondition !== 'occurrences',
                  )} */}
                </div>
              )}
            />
          </div>
        </div>
        <div className="flex justify-end px-[32px]">
          <div className="flex items-center gap-2">
            <AppButton onClick={handleCancel}>ביטול</AppButton>

            <AppButton
              variant="primary"
              onClick={handleSubmit(handleFormSubmit)}>
              שמירה
            </AppButton>
          </div>
        </div>
      </div>
    </AppDialog>
  );
}

export default CustomTiming;
